import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Clock, User, ArrowRight } from 'lucide-react';

export default function AuditLogDisplay() {
  const { t } = useTranslation();

  // Helper function to format relative time with translations
  const formatDistanceToNow = (date) => {
    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return t('settings.permissions.audit.daysAgo', { count: days });
    if (hours > 0) return t('settings.permissions.audit.hoursAgo', { count: hours });
    if (minutes > 0) return t('settings.permissions.audit.minutesAgo', { count: minutes });
    return t('settings.permissions.audit.justNow');
  };

  const { data: auditLogs, isLoading } = useQuery({
    queryKey: ['permissions', 'audit-log'],
    queryFn: () => api.permissions.getAuditLog({ page_size: 20 })
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.permissions.auditLog')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground">
            {t('common.loading')}
          </div>
        </CardContent>
      </Card>
    );
  }

  const logs = auditLogs?.results || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.permissions.auditLog')}</CardTitle>
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {t('settings.permissions.noAuditLogs')}
          </div>
        ) : (
          <div className="space-y-3">
            {logs.map(log => {
              const hasChanges = (
                log.old_can_view !== log.new_can_view ||
                log.old_can_edit !== log.new_can_edit
              );

              return (
                <div
                  key={log.id}
                  className="rounded-lg border border-border bg-background p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-foreground">
                          {t('settings.permissions.roles.' + log.role)}
                        </span>
                        <span className="text-muted-foreground">-</span>
                        <span className="font-medium text-sm text-foreground">
                          {t('settings.permissions.fields.' + log.field_name)}
                        </span>
                      </div>

                      {hasChanges && (
                        <div className="mt-2 flex items-center gap-4 text-xs">
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">{t('settings.permissions.viewLabel')}:</span>
                            <span className={log.old_can_view ? 'text-green-600' : 'text-red-600'}>
                              {log.old_can_view ? t('common.yes') : t('common.no')}
                            </span>
                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                            <span className={log.new_can_view ? 'text-green-600' : 'text-red-600'}>
                              {log.new_can_view ? t('common.yes') : t('common.no')}
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">{t('settings.permissions.editLabel')}:</span>
                            <span className={log.old_can_edit ? 'text-green-600' : 'text-red-600'}>
                              {log.old_can_edit ? t('common.yes') : t('common.no')}
                            </span>
                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                            <span className={log.new_can_edit ? 'text-green-600' : 'text-red-600'}>
                              {log.new_can_edit ? t('common.yes') : t('common.no')}
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span>{log.changed_by_name || t('settings.permissions.audit.unknownUser')}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          <span>
                            {formatDistanceToNow(new Date(log.changed_at))}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
